import BaseModel from './BaseModel';
import {HitAdminUiEnums} from '../data/HitAdminUiEnums';
import {translate} from '../../plugins/i18n/i18n';
import {StringAttribute} from '../../components/container/attributes/StringAttribute.ts';
import {BooleanAttribute} from '../../components/container/attributes/BooleanAttribute.ts';
import {EnumerationAttribute} from '../../components/container/attributes/EnumerationAttribute.ts';
import {JsonObjectAttribute} from '../../components/container/attributes/JsonObjectAttribute.ts';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {required} from '@vuelidate/validators';

export default class Tracker extends BaseModel {
  constructor({
    id,
    serialNo = undefined,
    imei = undefined,
    simNo = undefined,
    model = undefined,
    category = undefined,
    badgingType = undefined,
    memo = undefined,
    active = undefined,
    status = undefined,
    lastSendMessage = undefined,
  }) {
    super(id, undefined, undefined);
    this.serialNo = serialNo;
    this.imei = imei;
    this.simNo = simNo;
    this.model = HitAdminUiEnums.getEnumValue('MODEL', model, translate);
    this.category = HitAdminUiEnums.getEnumValue(
      'CATEGORY',
      category,
      translate,
    );
    this.badgingType = HitAdminUiEnums.getEnumValue(
      'BADGING_TYPE',
      badgingType,
      translate,
    );
    this.memo = memo;
    this.active = active;
    this.status = HitAdminUiEnums.getEnumValue('STATUS', status, translate);
    this.lastSendMessage = lastSendMessage;
  }

  get fullDesignation() {
    return HitUtils.createStandardDesignation({
      number: this.serialNo,
      designation: this.imei + ' - ' + this.simNo,
    });
  }

  static get createInitColumns() {
    return {
      serial_no: '',
      imei: '',
      sim_no: '',
      model: 'APLICOM_A9_NEX',
      category: 'IOT',
      badging_type: null,
      active: true,
    };
  }

  static get apiRoute() {
    return 'tracker';
  }
  static get detailPageRoute() {
    return 'trackerDetail';
  }
  static get listPageRoute() {
    return 'trackerList';
  }
  static get config() {
    return {
      addable: true,
      importable: false,
      deletable: false,
    };
  }

  get apiRoute() {
    return Tracker.apiRoute;
  }
  get detailPageRoute() {
    return Tracker.detailPageRoute;
  }
  get listPageRoute() {
    return Tracker.listPageRoute;
  }
  get config() {
    return Tracker.config;
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'serialNo',
      'imei',
      'simNo',
      'model',
      'category',
      'badgingType',
      'status',
      'active',
    ]);
  }
  static get entityListDisplayAttrs() {
    return [
      'serialNo',
      'imei',
      'simNo',
      'model',
      'category',
      'badgingType',
      'memo',
      'status',
      'active',
      'lastSendMessage',
    ];
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'serialNo',
      'imei',
      'simNo',
      'model',
      'category',
      'badgingType',
      'memo',
      'status',
      'active',
      'lastSendMessage',
    ]);
  }

  static get panelDisplayAttrs() {
    return this.getAttributes(
      [
        'serialNo',
        'imei',
        'simNo',
        'model',
        'category',
        'badgingType',
        'memo',
        'status',
        'active',
        'lastSendMessage',
      ],
      ['creationDate'],
    );
  }

  static get joinAttrsKey() {
    return [
      'serialNo',
      'imei',
      'simNo',
      'model',
      'category',
      'badgingType',
      'memo',
      'status',
      'active',
      'lastSendMessage',
    ];
  }

  static get allAttributes() {
    return [
      new StringAttribute({
        name: 'serialNo',
        column: 'serial_no',
        tableProperties: {
          header: 'hit-admin.tracker.serial-no',
          maxWidth: '1fr',
        },
      }),
      new StringAttribute({
        name: 'imei',
        column: 'imei',
        tableProperties: {
          header: 'hit-admin.tracker.imei',
          maxWidth: '1fr',
        },
      }),
      new StringAttribute({
        name: 'simNo',
        column: 'sim_no',
        tableProperties: {
          header: 'hit-admin.tracker.sim-no',
          maxWidth: '1fr',
        },
      }),
      new EnumerationAttribute({
        name: 'model',
        enumType: 'MODEL',
        enumClass: HitAdminUiEnums,
        column: 'model',
        validations: {required},
        tableProperties: {
          header: 'hit-admin.tracker.model',
          maxWidth: '1fr',
        },
      }),
      new EnumerationAttribute({
        name: 'category',
        enumType: 'CATEGORY',
        enumClass: HitAdminUiEnums,
        column: 'category',
        validations: {required},
        tableProperties: {
          header: 'hit-admin.tracker.category',
          maxWidth: '1fr',
        },
      }),
      new EnumerationAttribute({
        name: 'badgingType',
        enumType: 'BADGING_TYPE',
        enumClass: HitAdminUiEnums,
        column: 'badging_type',
        tableProperties: {
          header: 'hit-admin.tracker.badging-type',
          maxWidth: '1fr',
        },
      }),
      new StringAttribute({
        name: 'memo',
        column: 'memo',
        tableProperties: {
          header: 'hit-admin.tracker.memo',
          maxWidth: '1fr',
        },
      }),
      new EnumerationAttribute({
        name: 'status',
        enumType: 'STATUS',
        enumClass: HitAdminUiEnums,
        column: 'status',
        tableProperties: {
          header: 'hit-admin.tracker.status',
          maxWidth: '1fr',
        },
      }),
      new BooleanAttribute({
        name: 'active',
        column: 'active',
        tableProperties: {
          header: 'hit-admin.tracker.active',
          maxWidth: '1fr',
        },
      }),
      new JsonObjectAttribute({
        name: 'lastSendMessage',
        column: 'last_send_message',
        tableProperties: {
          header: 'hit-admin.tracker.last-send-message',
          maxWidth: '1fr',
        },
      }),
    ];
  }
}
